import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { SortingState, PaginationState } from '@tanstack/react-table'
import { UserPlus } from '@phosphor-icons/react'
import { userApi, type ListUsersParams } from '@/api/users'
import type { OrganizationDetail } from '@/api/organizations'
import { UserDataTable } from '@/components/users/UserDataTable'
import { getColumns } from '@/components/users/UserColumns'
import { UserFilters } from '@/components/users/UserFilters'
import { UserProvisionSheet } from '@/components/users/UserProvisionSheet'
import { Button } from '@/components/ui/button'

interface OrgUsersTabProps {
  orgId: string
  org: OrganizationDetail
}

export function OrgUsersTab({ orgId }: OrgUsersTabProps) {
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined)
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null)

  const params: ListUsersParams = {
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
    ...(search && { search }),
    ...(roleFilter && { role_slug: roleFilter }),
    ...(statusFilter !== null && { is_active: statusFilter }),
    ...(sorting.length > 0 && {
      sort_by: sorting[0].id,
      sort_order: sorting[0].desc ? 'desc' as const : 'asc' as const,
    }),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['users', orgId, params],
    queryFn: () => userApi.list(orgId, params),
    enabled: !!orgId,
  })

  const columns = getColumns()

  function handleSearchChange(value: string) {
    setSearch(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  function handleRoleChange(value: string | undefined) {
    setRoleFilter(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  function handleStatusChange(value: boolean | null) {
    setStatusFilter(value)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Users</h2>
        <Button size="sm" onClick={() => setProvisionOpen(true)}>
          <UserPlus className="h-4 w-4 mr-1.5" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <UserFilters
        search={search}
        onSearchChange={handleSearchChange}
        roleSlug={roleFilter}
        onRoleChange={handleRoleChange}
        isActive={statusFilter}
        onStatusChange={handleStatusChange}
        orgId={orgId}
      />

      {/* Data table */}
      <UserDataTable
        data={data?.items ?? []}
        columns={columns}
        total={data?.total ?? 0}
        page={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        orgId={orgId}
      />

      {/* Provisioning sheet */}
      <UserProvisionSheet
        open={provisionOpen}
        onOpenChange={setProvisionOpen}
        orgId={orgId}
      />
    </div>
  )
}
